// src/utils/lossRunScoreboard.js
//
// Plain words for the Loss Runs scoreboard: per policy, "do I hold the paper?"
// and "is it handled this renewal?". The open card's table, the collapsed
// card's summary line and the grid chip popover all read these, so the
// wording is identical everywhere. Pure; nothing here changes a color — the
// color comes from lossRunStatus.policyState and is only put into words.

import { dateOnlyOf, termOf } from './lossRunStatus';
import { needForLob, NEED_KEYS } from './lossRunGrid';
import { isPolicyOpen, isHandled, isStillOpen } from './lossRunResolutions';
import { displayDate } from './buildHandoffModel';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const blank = v => v === null || v === undefined || String(v).trim() === '';
const isLetter = r => /no known loss letter/i.test(String(r?.Method ?? ''));

// "2026-10-05T07:00:00Z" -> "Oct 2026". First ten characters only; no local time.
export function monthYear(v) {
  const d = dateOnlyOf(v);
  if (!d) return null;
  const [y, m] = d.split('-');
  return `${MONTHS[Number(m) - 1] || m} ${y}`;
}

/**
 * paperWords(state) — state from lossRunStatus.policyState
 *   { color, reasons, latestRequest }
 * → { color, words, vaultPath, old, waiting }
 *   green  "Loss run, Oct 2026" | "Signed no-loss letter, Oct 2026"
 *   yellow "Old copy, Mar 2025" | "Requested 10/01/2026, waiting"
 *   red    "Nothing on file"
 *   grey   its reasons
 * vaultPath is set only when a copy is on file (green, or yellow old copy).
 */
export function paperWords(state) {
  const color = state?.color || 'red';
  const latest = state?.latestRequest || null;
  const vaultPath = latest && !blank(latest.VaultPath) ? String(latest.VaultPath).trim() : '';
  if (color === 'grey') return { color, words: (state?.reasons || []).join(' · ') || 'Blocked', vaultPath: '', old: false, waiting: false };
  if (color === 'red') return { color, words: 'Nothing on file', vaultPath: '', old: false, waiting: false };
  const received = dateOnlyOf(latest?.DateReceived);
  const when = monthYear(latest?.ValuedAsOf) || monthYear(received);
  if (color === 'green') {
    return { color, words: `${isLetter(latest) ? 'Signed no-loss letter' : 'Loss run'}${when ? `, ${when}` : ''}`, vaultPath, old: false, waiting: false };
  }
  if (received) return { color, words: `Old copy${when ? `, ${when}` : ''}`, vaultPath, old: true, waiting: false };
  const req = displayDate(latest?.DateRequested);
  return { color, words: req ? `Requested ${req}, waiting` : 'Requested, waiting', vaultPath: '', old: false, waiting: true };
}

/**
 * handledWords({ policy, color, entry }) — entry from
 * lossRunResolutions.resolutionIndex ({ resolution, handled, stillOpen }) or null
 * → { kind: 'handled'|'stillOpen'|'nothingNeeded'|'notUsual'|'notRecorded', words, muted }
 */
export function handledWords({ policy, color, entry } = {}) {
  const res = entry?.resolution || null;
  if (res && isHandled(res)) {
    const reason = blank(res.Reason) ? 'reason not on record' : String(res.Reason).trim();
    const by = blank(res.DecidedBy) ? 'decider not on record' : String(res.DecidedBy).trim();
    const when = displayDate(res.DecidedDate) || 'date not on record';
    return { kind: 'handled', words: `✓ ${reason}, per ${by}, ${when}`, muted: false };
  }
  if (res && isStillOpen(res)) {
    return { kind: 'stillOpen', words: `Still open: ${blank(res.Notes) ? '(no note)' : String(res.Notes).trim()}`, muted: false };
  }
  if (color === 'green') return { kind: 'nothingNeeded', words: 'Nothing needed', muted: false };
  if (needForLob(policy?.LOB) === 'notUsual') return { kind: 'notUsual', words: 'Not usually requested', muted: true };
  return { kind: 'notRecorded', words: 'Not recorded', muted: false };
}

/**
 * policyLabel(policy, source) → { carrier, number, partOf, text }
 * partOf is the source name when the source is a Carrier whose name differs
 * from the policy's carrier (a member company of a group), else ''.
 * text: "Monroe Guaranty Insurance Company · ABC123 (part of FCCI Insurance Group)"
 */
export function policyLabel(policy, source) {
  const carrier = blank(policy?.Carrier) ? 'Carrier unknown' : String(policy.Carrier).trim();
  const number = blank(policy?.PolicyNumber) ? 'no number' : String(policy.PolicyNumber).trim();
  const srcName = String(source?.Title ?? '').trim();
  const isCarrier = String(source?.Kind ?? '').trim().toLowerCase() === 'carrier';
  const partOf = isCarrier && srcName && srcName.toLowerCase() !== carrier.toLowerCase() ? srcName : '';
  return { carrier, number, partOf, text: `${carrier} · ${number}${partOf ? ` (part of ${partOf})` : ''}` };
}

/**
 * buildScoreboard({ policies, source, policyStates, handled })
 *   policies     — the eligible policies of ONE source (or of one grid chip)
 *   source       — the FIG_LossRunSources row, or null (Unassigned)
 *   policyStates — Map(policyId -> policyState result)
 *   handled      — Map(policyId -> resolutionIndex entry)
 * → {
 *     rows:   [{ id, policy, term, line, need, label, paper, handled, open }]   // flat, in display order
 *     groups: [{ term, rows }]                                                  // newest term first
 *     counts: { open, handled, current, old, total }
 *   }
 * Within a term: need group (required, letter, not usual, unclassified), then line, then label.
 * open uses the existing rule (lossRunResolutions.isPolicyOpen).
 */
export function buildScoreboard({ policies = [], source = null, policyStates, handled } = {}) {
  const rows = (policies || []).filter(Boolean).map(p => {
    const id = String(p.id);
    const st = policyStates?.get?.(id) || { color: 'red', reasons: ['Not requested'], latestRequest: null };
    const entry = handled?.get?.(id) || null;
    return {
      id,
      policy: p,
      term: termOf(p) || 'Term unknown',
      line: blank(p.LOB) ? 'Line not on record' : String(p.LOB).trim(),
      need: needForLob(p.LOB),
      label: policyLabel(p, source),
      paper: paperWords(st),
      handled: handledWords({ policy: p, color: st.color, entry }),
      open: isPolicyOpen({ policy: p, color: st.color, resolution: entry?.resolution || null }),
    };
  });
  const byTerm = new Map();
  for (const r of rows) {
    if (!byTerm.has(r.term)) byTerm.set(r.term, []);
    byTerm.get(r.term).push(r);
  }
  const groups = [...byTerm.entries()]
    .sort((a, b) => (a[0] === 'Term unknown') - (b[0] === 'Term unknown') || b[0].localeCompare(a[0]))
    .map(([term, list]) => ({
      term,
      rows: list.sort((a, b) => NEED_KEYS.indexOf(a.need) - NEED_KEYS.indexOf(b.need) || a.line.localeCompare(b.line) || a.label.text.localeCompare(b.label.text)),
    }));
  const flat = groups.flatMap(g => g.rows);
  const counts = {
    open: flat.filter(r => r.open).length,
    handled: flat.filter(r => r.handled.kind === 'handled').length,
    current: flat.filter(r => r.paper.color === 'green').length,
    old: flat.filter(r => r.paper.old).length,
    total: flat.length,
  };
  return { rows: flat, groups, counts };
}

// "2 open · 1 handled · 3 current on file" (+ " · 1 old copy" when asked)
export function scoreboardSummary(counts, { oldCopies = false } = {}) {
  const c = counts || {};
  const parts = [`${c.open || 0} open`, `${c.handled || 0} handled`, `${c.current || 0} current on file`];
  if (oldCopies) parts.push(`${c.old || 0} old ${c.old === 1 ? 'copy' : 'copies'}`);
  return parts.join(' · ');
}
